'use client';

import * as React from 'react';
import { type ReactNode } from 'react';
import { Box } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';

import type { UserRole } from '@/types/v2/user-role';
import { paths } from '@/paths';
import { Page as NotFoundPage } from '@/pages/not-found';
import { logger } from '@/lib/default-logger';
import { useUser } from '@/hooks/v2/use-user';

export interface RoleGuardProps {
  children: ReactNode;
  requiredUserRoles?: UserRole[];
  requireAllUserRoles?: boolean;
  requiredCommunityRoles?: string[];
  requireAllCommunityRoles?: boolean;
  requiredAccountRoles?: string[];
  requireAllAccountRoles?: boolean;
  redirect?: boolean;
  displayUnauthorized?: boolean;
  show404?: boolean;
}

export function RoleGuard({
  children,
  requiredUserRoles,
  requireAllUserRoles = false,
  requiredCommunityRoles,
  requireAllCommunityRoles = false,
  requiredAccountRoles = [],
  requireAllAccountRoles = false,
  redirect = false,
  displayUnauthorized = false,
  show404 = false,
}: RoleGuardProps): JSX.Element | null {
  const navigate = useNavigate();
  const { user, error, community, account, isLoading } = useUser();
  const [isChecking, setIsChecking] = React.useState<boolean>(true);

  /**
   * Determines if the current user should be granted permission.
   *
   * The logic is:
   *   1. If a non-empty array is provided via `requiredUserRoles`, then check against user.roles.
   *      If the user has (all or any, depending on requireAllUserRoles) the required role(s),
   *      access is granted – otherwise, not.
   *
   *   2. Else if non-empty `requiredAccountRoles` is provided, then check account.roles.
   *
   *   3. Else if non-empty `requiredCommunityRoles` is provided, then check community.roles.
   *
   *   4. If none are provided, then access is granted by default.
   *
   * In this way, the user roles "supersede" the account and community roles.
   */
  const getHasAccess = () => {
    // First check user roles (highest priority)
    if (requiredUserRoles && requiredUserRoles.length > 0) {
      return user && user.roles
        ? requireAllUserRoles
          ? requiredUserRoles.every((role) => user.roles.includes(role))
          : requiredUserRoles.some((role) => user.roles.includes(role))
        : false;
    }

    // Check community roles first - if user has required community roles, grant access
    if (requiredCommunityRoles && requiredCommunityRoles.length > 0) {
      if (community && (community.roles)) {
        const hasCommunityAccess = requireAllCommunityRoles
          ? requiredCommunityRoles.every((role) => (community.roles).includes(role))
          : requiredCommunityRoles.some((role) => (community.roles).includes(role));

        if (hasCommunityAccess) {
          return true;
        }
      }
    }

    // If no community access, check account roles
    if (requiredAccountRoles && requiredAccountRoles.length > 0) {
      if (account && account.roles) {
        return requireAllAccountRoles
          ? requiredAccountRoles.every((role) => account.roles.includes(role))
          : requiredAccountRoles.some((role) => account.roles.includes(role));
      }
      // If account roles don't exist, fall back to community roles
      if (community && (community.roles)) {
        return requireAllAccountRoles
          ? requiredAccountRoles.every((role) => (community.roles).includes(role))
          : requiredAccountRoles.some((role) => (community.roles).includes(role));
      }
      return false;
    }

    return true;
  };

  const checkPermissions = async (): Promise<void> => {
    if (isLoading || error || !user) {
      setIsChecking(false);
      if (!user) {
        logger.debug('[AuthGuard]: User is not logged in, redirecting to sign in');
        navigate(paths.auth.signIn, { replace: true });
      }
      return;
    }

    const access = getHasAccess();
    if (!access && !displayUnauthorized && !show404) {
      logger.debug('[AuthGuard]: User does not have the necessary permissions, redirecting');
      if (redirect) {
        navigate(paths.notAuthorized, { replace: true });
      }
      return;
    }

    setIsChecking(false);
  };

  React.useEffect(() => {
    checkPermissions().catch(() => {
      // noop
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Expected
  }, [user, error, isLoading]);

  const hasAccess = getHasAccess();

  if (isChecking) {
    return null;
  }

  if (!hasAccess && displayUnauthorized && !show404) {
    return (
      <Box alignItems="center" display="flex" height="100vh" justifyContent="center">
        <Alert severity="error">You are not authorized to access this page</Alert>
      </Box>
    );
  }

  if (!hasAccess && show404) {
    return <NotFoundPage />;
  }

  if (error) {
    return <Alert color="error">{error}</Alert>;
  }

  return <>{children}</>;
}
